import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Pages from 'common/src/app/data/enum/Pages';
import ComponentType from 'common/src/app/data/enum/ComponentType';
import ContactUsFieldNames from 'common/src/app/data/enum/FieldNames/ContactUsFieldNames';
import InputType from 'common/src/app/data/enum/InputType';
import { withFunctionalClassName } from 'common/src/app/util/componentClassNameUtils';
import ContactFormType from './enum/ContactFormType';
import Textarea from '../../atoms/Textarea';
import Button from '../../atoms/Button';
import Select from '../../atoms/Select';
import FormikError from '../../atoms/FormikError';
import TextNew, { Colors, ElementTypes, FontWeight } from '../../atoms/TextNew';
import Loader from '../../atoms/Loader';
import ContactUsSuccessMessage from '../../molecules/ContactUsSuccessMessage';

import ContactUsPublicForm from './components/molecules/ContactUsPublicForm';

import './contact-us-form.scss';

const ContactUsForm = (
  {
    values,
    successMessageSubjectId,
    errors,
    children,
    handleSubmit,
    handleChange,
    handleBlur,
    setSuccessMessage,
    setFieldValue,
    setErrors,
    hasErrorBoundary,
    fetchSubjectOptions,
    isValid,
    isSubmitting,
    loading,
  },
  { getMessage },
  className,
  dataTestId,
) => {
  const [subjects, setSubjects] = useState([]);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(true);
  const contactFormRef = useRef(null);

  const { subjectId, formType } = values;
  const currentSubject = subjects.find(s => s.value === values.subjectId);
  const showThirdPartyDisclaimer = currentSubject?.requiresThirdPartyDataSharing;
  let localeMessageType = '';

  if (formType === ContactFormType.ADVERTISEMENT) {
    localeMessageType = 'Advertisement';
  }

  useEffect(() => () => setSuccessMessage?.(null), []);

  useEffect(() => {
    const loadSelectOptions = async () => {
      try {
        const retrievedSubjects = await fetchSubjectOptions();

        if (!Array.isArray(retrievedSubjects) || retrievedSubjects.length === 0) {
          setIsLoadingSubjects(false);
          setErrors({ generalError: 'contactUsForm.error.subjects' });
          return;
        }

        let sortedSubjects = retrievedSubjects.sort((a, b) => a.displayOrder - b.displayOrder);
        sortedSubjects = [
          { title: getMessage('contactUsForm.placeholder.subject'), value: '' },
          ...sortedSubjects,
        ];

        setIsLoadingSubjects(false);
        setSubjects(sortedSubjects);
      } catch (error) {
        setIsLoadingSubjects(false);
        setErrors({ generalError: 'contactUsForm.error.subjects' });
      }
    };

    loadSelectOptions();
  }, []);

  return (
    <div className={className} ref={contactFormRef}>
      {successMessageSubjectId ? (
        <header>
          <ContactUsSuccessMessage />
        </header>
      ) : (
        <>
          <TextNew.Sans.SM
            weight={FontWeight.LIGHT}
            localeId="contactUsForm.body"
            localeParams={{
              PREFER: (
                <TextNew.Sans.SM
                  key="contact-us-prefer-email"
                  weight={FontWeight.NORMAL}
                  localeId="contactUsForm.prefer"
                  element={ElementTypes.SPAN}
                />
              ),
            }}
          />
          {isLoadingSubjects && <Loader />}
          {subjects.length > 1 && (
            <form onSubmit={handleSubmit}>
              <Select
                customLabel={getMessage('contactUsForm.placeholder.subject')}
                defaultValue={subjectId}
                onChange={e => {
                  const value = e.target.value;
                  const selected = subjects.find(s => s.value === value);

                  setFieldValue(ContactUsFieldNames.SUBJECT_ID, value);
                  setFieldValue(ContactUsFieldNames.FORM_TYPE, selected?.formType);
                }}
                name={ContactUsFieldNames.SUBJECT_ID}
                options={subjects}
                disabled={hasErrorBoundary}
              />
              {subjectId !== '' && (
                <>
                  {children}
                  <Textarea
                    data-testid={`${dataTestId}-message`}
                    rows="4"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name={ContactUsFieldNames.MESSAGE}
                    type={InputType.TEXT}
                    placeholder={getMessage(`contactUsForm.contactMessage${localeMessageType}`)}
                  />
                  <FormikError name={ContactUsFieldNames.MESSAGE} />
                  {showThirdPartyDisclaimer && (
                    <TextNew.Sans.SM
                      element={ElementTypes.SMALL}
                      weight={FontWeight.LIGHT}
                      localeId="contactUsForm.thirdPartyDisclaimer"
                    />
                  )}
                  <small data-testid="privacy-policy-disclaimer">
                    <TextNew.Sans.SM
                      element={ElementTypes.SPAN}
                      weight={FontWeight.LIGHT}
                      localeId="contactUsForm.privacyPolicy.disclaimer"
                    />
                    <a href={Pages.PRIVACYPOLICY} target="_blank">
                      <TextNew.Sans.SM
                        localeId="contactUsForm.privacyPolicy.linkLabel"
                        element={ElementTypes.SPAN}
                        weight={FontWeight.LIGHT}
                        color={Colors.BRAND}
                      />
                    </a>
                    .
                  </small>
                  <Button
                    type={InputType.SUBMIT}
                    dataTestid={`${dataTestId}-button`}
                    disabled={!isValid || isSubmitting}
                    isLoading={isSubmitting || loading}
                  >
                    <TextNew.Sans.SM
                      element={ElementTypes.SPAN}
                      color={Colors.PRIMARY_LIGHT}
                      localeId="contactUsForm.cta"
                    />
                  </Button>
                </>
              )}
            </form>
          )}
        </>
      )}
      {errors?.generalError && (
        <TextNew.Error localeId={errors.generalError} cid="error" color={Colors.BRAND} />
      )}
    </div>
  );
};

ContactUsForm.propTypes = {
  children: PropTypes.node,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  successMessageSubjectId: PropTypes.string,
  setSuccessMessage: PropTypes.func,
  setFieldValue: PropTypes.func,
  setErrors: PropTypes.func,
  loading: PropTypes.bool,
  hasErrorBoundary: PropTypes.bool,
  fetchSubjectOptions: PropTypes.func,
  isValid: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

ContactUsForm.contextTypes = {
  getMessage: PropTypes.func.isRequired,
};

export { ContactUsPublicForm };

export default withFunctionalClassName(ComponentType.ORGANISM, 'ContactUsForm')(ContactUsForm);
